import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, Tooltip, useMapEvents, Circle } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import HeatmapLayer from './HeatmapLayer';
import { ClusterLayer } from './map/ClusterLayer';
import { ClusterPanel } from './map/ClusterPanel';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ExternalLink, MapPin, Users, Navigation, Flame, User, Calendar, Search, Crown, Compass } from 'lucide-react';

// Build "Cases like this" explore URL from case data
const buildCasesLikeThisUrl = (caseData, isPro = false) => {
  const params = new URLSearchParams();
  params.set('source', 'map_like_this'); // Track origin
  
  // Case type via source filter
  if (caseData.source === 'namus_unidentified') {
    params.set('filter_source', 'namus_unidentified');
  } else if (caseData.source === 'namus_unclaimed') {
    params.set('filter_source', 'namus_unclaimed');
  } else if (caseData.source && caseData.source !== 'community') {
    params.set('filter_source', caseData.source);
  }
  
  // Sex/Gender
  const gender = caseData.gender?.toLowerCase();
  if (gender) {
    if (gender.includes('male') && !gender.includes('female')) {
      params.set('gender', 'male');
    } else if (gender.includes('female')) {
      params.set('gender', 'female');
    }
  }
  
  // Age category (map to the dropdown values: child, teen, young_adult, adult, senior)
  if (caseData.age && !isNaN(caseData.age)) {
    const age = parseInt(caseData.age);
    if (age <= 12) {
      params.set('age', 'child');
    } else if (age <= 17) {
      params.set('age', 'teen');
    } else if (age <= 25) {
      params.set('age', 'young_adult');
    } else if (age <= 64) {
      params.set('age', 'adult');
    } else {
      params.set('age', 'senior');
    }
  }
  
  // State
  if (caseData.state) {
    params.set('state', caseData.state);
  }
  
  // Year range - use text search with year for simplicity
  const dateStr = caseData.date_missing || caseData.missing_date || caseData.last_seen_date || caseData.date_found;
  if (dateStr) {
    try {
      const year = new Date(dateStr).getFullYear();
      if (!isNaN(year) && year > 1900 && year <= new Date().getFullYear()) {
        // Map to year filter categories
        const currentYear = new Date().getFullYear();
        const yearsAgo = currentYear - year;
        if (yearsAgo <= 1) {
          params.set('year', 'last_year');
        } else if (yearsAgo <= 5) {
          params.set('year', '1_5_years');
        } else if (yearsAgo <= 10) {
          params.set('year', '5_10_years');
        } else {
          params.set('year', '10_plus_years');
        }
      }
    } catch (e) { /* ignore date parsing errors */ }
  }
  
  // Pro users get geo radius filtering (if explore page supports it in future)
  if (isPro && caseData.latitude && caseData.longitude) {
    params.set('lat', caseData.latitude.toFixed(4));
    params.set('lng', caseData.longitude.toFixed(4));
    params.set('radius', '100');
  }
  
  return `/browse?${params.toString()}`;
};

// Debounce utility for preventing rapid state updates
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Haversine formula to calculate distance between two coordinates in miles
const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Calculate nearby cases for a given case
const calculateNearbyCases = (targetCase, allCases, radiusMiles = 100) => {
  if (!targetCase?.latitude || !targetCase?.longitude) return null;
  
  const targetYear = targetCase.date_missing ? new Date(targetCase.date_missing).getFullYear() : null;
  const isMissing = targetCase.source !== 'namus_unidentified' && targetCase.source !== 'namus_unclaimed';
  
  let nearbyUnidentified = 0;
  let nearbyMissing = 0;
  let nearbyUnclaimed = 0;
  let sameYearNearby = 0;
  
  for (const c of allCases) {
    if (!c.latitude || !c.longitude || c.id === targetCase.id) continue;
    
    const distance = calculateDistanceMiles(
      targetCase.latitude, targetCase.longitude,
      c.latitude, c.longitude
    );
    
    if (distance <= radiusMiles) {
      // Count by type
      if (c.source === 'namus_unidentified') {
        nearbyUnidentified++;
      } else if (c.source === 'namus_unclaimed') {
        nearbyUnclaimed++;
      } else {
        nearbyMissing++;
      }
      
      // Count same year (if target has a date)
      if (targetYear && c.date_missing) {
        const cYear = new Date(c.date_missing).getFullYear();
        if (Math.abs(cYear - targetYear) <= 1) { // Within 1 year
          sameYearNearby++;
        }
      }
    }
  }
  
  return {
    nearbyUnidentified,
    nearbyMissing,
    nearbyUnclaimed,
    sameYearNearby,
    isMissing,
    radiusMiles
  };
};

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons with source-based colors
const getMarkerColor = (source, isUrgent) => {
  if (isUrgent) return { bg: '#ef4444', border: '#dc2626' };
  switch (source) {
    case 'ncmec': return { bg: '#3b82f6', border: '#2563eb' };
    case 'namus': return { bg: '#22c55e', border: '#16a34a' };
    case 'namus_unidentified': return { bg: '#8b5cf6', border: '#7c3aed' };
    case 'namus_unclaimed': return { bg: '#06b6d4', border: '#0891b2' };
    case 'charley': return { bg: '#f59e0b', border: '#d97706' };
    case 'doenetwork': return { bg: '#ec4899', border: '#db2777' };
    case 'community': return { bg: '#f97316', border: '#ea580c' };
    default: return { bg: '#f97316', border: '#ea580c' };
  }
};

const createCustomIcon = (source, isUrgent) => {
  const colors = getMarkerColor(source, isUrgent);
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="relative">
        <div style="width: 28px; height: 28px; background: ${colors.bg}; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; ${isUrgent ? 'animation: pulse 2s infinite;' : ''}">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
        <div style="position: absolute; bottom: -4px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 6px solid ${colors.bg};"></div>
      </div>
    `,
    iconSize: [28, 34],
    iconAnchor: [14, 34],
    popupAnchor: [0, -34]
  });
};

// Waypoint label colors
const getWaypointColor = (label) => {
  switch (label) {
    case 'last_seen': return '#ef4444';
    case 'sighting': return '#f59e0b';
    case 'home': return '#3b82f6';
    case 'work': return '#22c55e';
    case 'found': return '#8b5cf6';
    case 'poi': return '#6b7280';
    default: return '#6b7280';
  }
};

// Create waypoint marker icon
const createWaypointIcon = (number, label) => {
  const color = getWaypointColor(label);
  
  return L.divIcon({
    className: 'waypoint-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 3px 12px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 14px;
        font-family: system-ui, -apple-system, sans-serif;
      ">
        ${number}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

// Custom cluster icon with gradient based on size
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size, fontSize, bgColor;
  
  if (count < 10) {
    size = 36;
    fontSize = 12;
    bgColor = 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
  } else if (count < 50) {
    size = 42;
    fontSize = 13;
    bgColor = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
  } else if (count < 100) {
    size = 48;
    fontSize = 14;
    bgColor = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
  } else {
    size = 54;
    fontSize = 15;
    bgColor = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  }
  
  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${bgColor};
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 3px 12px rgba(0,0,0,0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: ${fontSize}px;
        font-family: system-ui, -apple-system, sans-serif;
      ">
        ${count >= 1000 ? Math.round(count/1000) + 'k' : count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size),
    iconAnchor: L.point(size / 2, size / 2),
  });
};

const getSourceBadge = (source) => {
  switch (source) {
    case 'ncmec': return { color: 'bg-blue-500', label: 'NCMEC' };
    case 'namus': return { color: 'bg-green-500', label: 'NamUs' };
    case 'namus_unidentified': return { color: 'bg-purple-500', label: 'Unidentified' };
    case 'namus_unclaimed': return { color: 'bg-cyan-500', label: 'Unclaimed' };
    case 'charley': return { color: 'bg-amber-500', label: 'Charley' };
    case 'doenetwork': return { color: 'bg-pink-500', label: 'Doe Network' };
    case 'community': return { color: 'bg-orange-500', label: 'Community' };
    default: return { color: 'bg-gray-500', label: source?.toUpperCase() || 'Unknown' };
  }
};

// Component to fit bounds when cases change
const FitBounds = ({ cases, shouldFit }) => {
  const map = useMap();
  const hasFitted = useRef(false);
  
  useEffect(() => {
    // Only fit bounds once when shouldFit becomes true
    if (shouldFit && cases.length > 0 && !hasFitted.current) {
      const validCases = cases.filter(c => c.latitude && c.longitude);
      if (validCases.length > 0) {
        const bounds = L.latLngBounds(validCases.map(c => [c.latitude, c.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
        hasFitted.current = true;
      }
    }
  }, [cases, map, shouldFit]);
  
  return null;
};

// Component to track viewport and notify parent of visible cases
const ViewportTracker = ({ cases, onVisibleCasesChange }) => {
  const map = useMap();
  
  const updateVisibleCases = useCallback(() => {
    if (!onVisibleCasesChange) return;
    
    const bounds = map.getBounds();
    const visibleCases = cases.filter(c => {
      if (!c.latitude || !c.longitude) return false;
      return bounds.contains([c.latitude, c.longitude]);
    });
    
    onVisibleCasesChange(visibleCases);
  }, [map, cases, onVisibleCasesChange]);
  
  useMapEvents({
    moveend: updateVisibleCases,
    zoomend: updateVisibleCases,
  });
  
  useEffect(() => {
    updateVisibleCases();
  }, [cases, updateVisibleCases]);
  
  return null;
};

// Component to notify parent of viewport bounds changes for dynamic loading
const ViewportBoundsTracker = ({ onViewportChange, onZoomChange }) => {
  const map = useMap();
  const lastNotifiedRef = useRef(null);
  
  const notifyViewportChange = useCallback(() => {
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    
    // Always notify of zoom changes
    if (onZoomChange) {
      onZoomChange(zoom);
    }
    
    if (!onViewportChange) return;
    
    // Create bounds object
    const boundsObj = {
      sw_lat: bounds.getSouthWest().lat,
      sw_lng: bounds.getSouthWest().lng,
      ne_lat: bounds.getNorthEast().lat,
      ne_lng: bounds.getNorthEast().lng,
    };
    
    // Create a key to check if bounds changed significantly
    const boundsKey = `${boundsObj.sw_lat.toFixed(3)},${boundsObj.sw_lng.toFixed(3)},${boundsObj.ne_lat.toFixed(3)},${boundsObj.ne_lng.toFixed(3)},${zoom}`;
    
    // Only notify if bounds changed significantly
    if (lastNotifiedRef.current !== boundsKey) {
      lastNotifiedRef.current = boundsKey;
      onViewportChange(boundsObj, zoom);
    }
  }, [map, onViewportChange, onZoomChange]);
  
  useMapEvents({
    moveend: notifyViewportChange,
    zoomend: notifyViewportChange,
  });
  
  // Notify on initial load
  useEffect(() => {
    // Small delay to ensure map is ready
    const timer = setTimeout(notifyViewportChange, 100);
    return () => clearTimeout(timer);
  }, [notifyViewportChange]);
  
  return null;
};

// Map controller component - exposes map methods to parent
const MapController = ({ onMapReady, selectedCaseId, cases }) => {
  const map = useMap();
  
  // Expose map reference to parent
  useEffect(() => {
    if (onMapReady) {
      onMapReady({
        flyTo: (lat, lng, zoom = 14) => {
          map.flyTo([lat, lng], zoom, { duration: 1.5 });
        },
        fitBounds: (bounds) => {
          map.fitBounds(bounds, { padding: [50, 50] });
        },
        getMap: () => map
      });
    }
  }, [map, onMapReady]);
  
  // Fly to selected case when it changes
  useEffect(() => {
    if (selectedCaseId && cases) {
      const selectedCase = cases.find(c => c.id === selectedCaseId);
      if (selectedCase && selectedCase.latitude && selectedCase.longitude) {
        map.flyTo([selectedCase.latitude, selectedCase.longitude], 14, { duration: 1.5 });
      }
    }
  }, [selectedCaseId, cases, map]);
  
  return null;
};

// Stats overlay component
const MapStats = ({ totalCases, visibleCases, clustered, showHeatmap, userLocation }) => {
  return (
    <div className="absolute top-3 left-3 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-sm" data-testid="map-stats-overlay">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <MapPin className="w-4 h-4" />
        <span>{visibleCases.toLocaleString()} visible</span>
      </div>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Users className="w-4 h-4" />
        <span>{totalCases.toLocaleString()} total loaded</span>
      </div>
      {clustered && !showHeatmap && (
        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
          Clustering enabled
        </div>
      )}
      {showHeatmap && (
        <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 mt-1">
          <Flame className="w-3 h-3" />
          Heatmap view
        </div>
      )}
      {userLocation && (
        <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 mt-1">
          <Navigation className="w-3 h-3" />
          Your location active
        </div>
      )}
    </div>
  );
};

// Viewport case count overlay component
const ViewportCaseCountOverlay = ({ caseCount, loading, currentZoom }) => {
  return (
    <div 
      className="absolute bottom-4 left-4 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-full shadow-lg px-4 py-2 flex items-center gap-2"
      data-testid="viewport-case-count"
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-t-transparent border-blue-500 rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading...</span>
        </>
      ) : (
        <>
          <Search className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium">
            <span className="text-blue-600 dark:text-blue-400">{caseCount.toLocaleString()}</span> cases in view
          </span>
          {currentZoom < 6 && (
            <span className="text-xs text-muted-foreground">(zoom in for more)</span>
          )}
        </>
      )}
    </div>
  );
};

// User location marker component
const UserLocationMarker = ({ position, radius = 50 }) => {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.setView(position, 10);
    }
  }, [map, position]);
  
  if (!position) return null;
  
  return (
    <>
      <Circle
        center={position}
        radius={radius * 1609.34}
        pathOptions={{
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.1,
          weight: 2,
          dashArray: '5, 5'
        }}
      />
      <Marker
        position={position}
        icon={L.divIcon({
          className: 'user-location-marker',
          html: `
            <div style="
              width: 20px;
              height: 20px;
              background: #3b82f6;
              border: 3px solid white;
              border-radius: 50%;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(0,0,0,0.3);
            "></div>
          `,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        })}
      >
        <Popup>
          <div className="text-sm font-medium">Your Location</div>
          <div className="text-xs text-muted-foreground">Showing cases within {radius} miles</div>
        </Popup>
      </Marker>
    </>
  );
};

export const MapComponent = ({ 
  cases = [], 
  corridors = [], 
  showCorridors = false, 
  center = [39.8283, -98.5795], 
  zoom = 4, 
  height = "600px", 
  className = "",
  enableClustering = true,
  showStats = true,
  onVisibleCasesChange = null,
  fitBoundsOnLoad = true,
  showHeatmap = false,
  userLocation = null,
  userLocationRadius = 50,
  onMapReady = null,
  selectedCaseId = null,
  routeWaypoints = [],
  routeRadius = 25,
  highlightedCaseIds = [],
  isPro = false,
  onViewportChange = null,
  loadingViewport = false,
  onClusterVisibilityChange = null
}) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [hasFittedBounds, setHasFittedBounds] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [clustersVisible, setClustersVisible] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterData, setClusterData] = useState([]);
  
  // Fetch investigative clusters when toggled on
  useEffect(() => {
    if (clustersVisible && !clusterData.length) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clusters/map-clusters`)
        .then(r => r.json())
        .then(data => setClusterData(data.clusters || []))
        .catch(() => {});
    }
  }, [clustersVisible, clusterData.length]);

  // Notify parent of cluster visibility changes
  useEffect(() => {
    if (onClusterVisibilityChange) {
      onClusterVisibilityChange(clustersVisible, clusterData);
    }
  }, [clustersVisible, clusterData, onClusterVisibilityChange]);
  
  // Memoize cases with valid coordinates
  const validCases = useMemo(() => {
    return cases.filter(c => c.latitude && c.longitude);
  }, [cases]);
  
  // Debounced setter for visible count to prevent popup flickering
  const debouncedSetVisibleCount = useMemo(
    () => debounce((count) => setVisibleCount(count), 300),
    []
  );
  
  // Handle visible cases updates (debounced to prevent popup flickering)
  const handleVisibleCasesChange = useCallback((visible) => {
    debouncedSetVisibleCount(visible.length);
    if (onVisibleCasesChange) {
      onVisibleCasesChange(visible);
    }
  }, [onVisibleCasesChange, debouncedSetVisibleCount]);
  
  // Fit bounds once on initial load
  useEffect(() => {
    if (validCases.length > 0 && !hasFittedBounds && fitBoundsOnLoad) {
      setHasFittedBounds(true);
    }
  }, [validCases, hasFittedBounds, fitBoundsOnLoad]);

  // Render individual marker
  const renderMarker = useCallback((person) => {
    const sourceBadge = getSourceBadge(person.source);
    
    // Calculate nearby cases for this person
    const nearbyCases = calculateNearbyCases(person, validCases, 100);
    
    // Format date missing
    const formatDateMissing = (date) => {
      if (!date) return null;
      try {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        return date;
      }
    };
    
    const dateMissing = formatDateMissing(person.date_missing || person.missing_date || person.last_seen_date);
    const dateFound = formatDateMissing(person.date_found || person.recovery_date);
    const hasPhoto = person.photo_url || person.image_url || person.thumbnail;

    // Case-type badge — separate from source badge so the popup shows
    // BOTH "Missing / Unidentified / Unclaimed" AND "NamUs / Doe Net /
    // NCMEC". The two are orthogonal axes.
    const caseTypeMeta = (() => {
      const ct = (person.case_type || '').toLowerCase();
      if (ct.includes('unidentified')) return { label: 'Unidentified', color: 'bg-purple-600' };
      if (ct.includes('unclaimed')) return { label: 'Unclaimed', color: 'bg-teal-600' };
      if (ct.includes('missing')) return { label: 'Missing', color: 'bg-orange-500' };
      // Source-derived fallback for older records that never got case_type populated
      if (person.source === 'namus_unidentified') return { label: 'Unidentified', color: 'bg-purple-600' };
      if (person.source === 'namus_unclaimed') return { label: 'Unclaimed', color: 'bg-teal-600' };
      return null;
    })();

    const reportingAgency = person.investigating_agency
      || person.agency
      || person.reporting_agency
      || person.le_agency
      || null;
    
    // Get photo URL with proxy for NamUs images
    const getPhotoUrl = () => {
      const url = person.photo_url || person.image_url || person.thumbnail;
      if (!url) return null;
      
      const API = process.env.REACT_APP_BACKEND_URL;
      
      // NamUs images need proxy
      if (url.includes('namus.gov') && url.includes('/Images/')) {
        const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnclaimedPersons|UnidentifiedPersons)\/Cases\/(\d+)\/Images\/(\d+)/);
        if (match) {
          const [, caseType, caseId, imageId] = match;
          return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
        }
        const fallbackMatch = url.match(/Cases\/(\d+)\/Images\/(\d+)/);
        if (fallbackMatch) {
          const [, caseId, imageId] = fallbackMatch;
          let caseType = 'MissingPersons';
          if (person.source === 'namus_unclaimed') caseType = 'UnclaimedPersons';
          else if (person.source === 'namus_unidentified') caseType = 'UnidentifiedPersons';
          return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
        }
      }
      
      return url;
    };
    
    const photoUrl = getPhotoUrl();
    
    return (
      <Marker
        key={person.id}
        position={[person.latitude, person.longitude]}
        icon={createCustomIcon(person.source, person.is_urgent)}
      >
        <Popup className="custom-popup" maxWidth={320} minWidth={280}>
          <div className="p-1">
            {/* Header with photo and basic info */}
            <div className="flex gap-3 mb-2">
              {/* Photo */}
              <div className="shrink-0">
                {hasPhoto ? (
                  <img 
                    src={photoUrl} 
                    alt={person.name}
                    className="w-16 h-20 object-cover rounded-lg shadow-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-16 h-20 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center ${hasPhoto ? 'hidden' : ''}`}
                >
                  <User className="w-8 h-8 text-slate-400" />
                </div>
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <h3 className="font-heading font-bold text-sm text-foreground leading-tight">
                    {person.name}
                  </h3>
                  <Badge className={`${sourceBadge.color} text-white border-0 text-[10px] shrink-0`}>
                    {sourceBadge.label}
                  </Badge>
                </div>

                {/* Case-type badge row (Missing / Unidentified / Unclaimed) */}
                <div className="flex items-center flex-wrap gap-1 mb-1">
                  {caseTypeMeta && (
                    <Badge
                      className={`${caseTypeMeta.color} text-white border-0 text-[10px]`}
                      data-testid={`popup-case-type-${caseTypeMeta.label.toLowerCase()}`}>
                      {caseTypeMeta.label}
                    </Badge>
                  )}
                  {person.is_urgent && (
                    <Badge className="bg-red-500 text-white border-0 text-[10px]">
                      <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                      URGENT
                    </Badge>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {person.age && `${person.age} years old`}
                  {person.age && (person.gender || person.race) && ' • '}
                  {person.gender}
                  {person.gender && person.race && ' • '}
                  {person.race}
                </p>

                {dateMissing && !dateFound && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    Missing since {dateMissing}
                  </p>
                )}
                {dateFound && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    Recovered {dateFound}
                  </p>
                )}
              </div>
            </div>
            
            {/* Location */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 mb-2">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">
                  {person.city && `${person.city}, `}
                  {person.state || person.last_seen_location || 'Location unknown'}
                </span>
              </p>
              {person.latitude && person.longitude && (
                <p className="text-[10px] text-muted-foreground/70 mt-0.5 pl-4 cursor-pointer hover:text-muted-foreground" 
                   onClick={(e) => {
                     e.stopPropagation();
                     // Copy coordinates to clipboard
                     navigator.clipboard.writeText(`${person.latitude.toFixed(6)}, ${person.longitude.toFixed(6)}`);
                   }}
                   title="Click to copy coordinates">
                  📍 View on map
                </p>
              )}
            </div>
            
            {person.nearestCorridor && (
              <div className="mb-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-xs border border-amber-200 dark:border-amber-800">
                <span className="font-medium text-amber-700 dark:text-amber-400">Near {person.nearestCorridor.name}</span>
                <span className="text-amber-600 dark:text-amber-500 ml-1">({person.nearestCorridor.distance} mi)</span>
              </div>
            )}
            
            {/* Physical description if available */}
            {(person.height || person.weight || person.hair_color || person.eye_color) && (
              <div className="text-[10px] text-muted-foreground mb-2 flex flex-wrap gap-x-2">
                {person.height && <span>{person.height}</span>}
                {person.weight && <span>{person.weight}</span>}
                {person.hair_color && <span>{person.hair_color} hair</span>}
                {person.eye_color && <span>{person.eye_color} eyes</span>}
              </div>
            )}

            {/* Reporting / investigating agency — desktop-only to keep
                the mobile popup compact */}
            {reportingAgency && (
              <div
                className="hidden md:flex text-[10px] text-muted-foreground mb-2 items-start gap-1"
                data-testid="popup-reporting-agency">
                <span className="font-semibold text-slate-600 dark:text-slate-400 shrink-0">
                  Agency:
                </span>
                <span className="truncate" title={reportingAgency}>
                  {reportingAgency}
                </span>
              </div>
            )}
            
            {/* Nearby Context - Bridge to Matching (Always show) */}
            {nearbyCases && (
              <div className="mb-3 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                  Nearby Context
                </p>
                <div className="space-y-1 text-xs">
                  {/* For missing persons, show nearby unidentified */}
                  {nearbyCases.isMissing ? (
                    nearbyCases.nearbyUnidentified > 0 ? (
                      <p className="text-slate-700 dark:text-slate-300">
                        <span className="font-semibold text-indigo-600 dark:text-indigo-400">{nearbyCases.nearbyUnidentified}</span> unidentified person{nearbyCases.nearbyUnidentified !== 1 ? 's' : ''} within {nearbyCases.radiusMiles} miles
                      </p>
                    ) : (
                      <p className="text-slate-500 dark:text-slate-400 italic">
                        No unidentified cases within {nearbyCases.radiusMiles} miles
                      </p>
                    )
                  ) : (
                    /* For unidentified, show nearby missing */
                    nearbyCases.nearbyMissing > 0 ? (
                      <p className="text-slate-700 dark:text-slate-300">
                        <span className="font-semibold text-indigo-600 dark:text-indigo-400">{nearbyCases.nearbyMissing}</span> missing person{nearbyCases.nearbyMissing !== 1 ? 's' : ''} within {nearbyCases.radiusMiles} miles
                      </p>
                    ) : (
                      <p className="text-slate-500 dark:text-slate-400 italic">
                        No missing persons within {nearbyCases.radiusMiles} miles
                      </p>
                    )
                  )}
                  
                  {/* Same year nearby */}
                  {nearbyCases.sameYearNearby > 0 && (
                    <p className="text-slate-700 dark:text-slate-300">
                      <span className="font-semibold text-indigo-600 dark:text-indigo-400">{nearbyCases.sameYearNearby}</span> case{nearbyCases.sameYearNearby !== 1 ? 's' : ''} from same year nearby
                    </p>
                  )}
                </div>
                
                {/* Action links - View potential matches + Cases like this */}
                <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-slate-600 space-y-1.5">
                  {/* View potential matches - only if nearby cases exist */}
                  {((nearbyCases.isMissing && nearbyCases.nearbyUnidentified > 0) || 
                    (!nearbyCases.isMissing && nearbyCases.nearbyMissing > 0)) && (
                    <Link 
                      to={`/potential-matches?caseId=${person.id}&source=map`}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                      data-testid="popup-view-matches-link"
                    >
                      <Search className="w-3 h-3" />
                      View potential matches →
                    </Link>
                  )}
                  
                  {/* Cases like this - always available */}
                  <Link 
                    to={buildCasesLikeThisUrl(person, isPro)}
                    className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                    title="Browse cases with similar basic details"
                    data-testid="popup-cases-like-this-link"
                  >
                    <Compass className="w-3 h-3" />
                    Cases like this →
                  </Link>
                </div>
                
                {/* Pro enhancement note for community users */}
                {!isPro && (nearbyCases.nearbyUnidentified > 0 || nearbyCases.nearbyMissing > 0) && (
                  <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-600">
                    <Link 
                      to="/pricing"
                      className="flex items-center gap-1.5 text-[10px] text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                    >
                      <Crown className="w-3 h-3" />
                      <span>Unlock distance & signal analysis</span>
                    </Link>
                  </div>
                )}
              </div>
            )}
            
            <Link to={`/case/${person.id}`}>
              <Button size="sm" className="w-full bg-trust-blue-500 hover:bg-trust-blue-600 active:bg-trust-blue-700 text-white rounded-full text-xs h-8">
                View Full Details
                <ExternalLink className="w-3 h-3 ml-1.5" />
              </Button>
            </Link>
          </div>
        </Popup>
      </Marker>
    );
  }, [validCases, isPro]);

  return (
    <div className={`relative rounded-3xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl ${className}`} style={{ height }} data-testid="map-wrapper">
      {/* Dim regular case markers when cluster overlay is active */}
      {clustersVisible && (
        <style>{`
          .custom-marker, .custom-cluster-icon, .marker-cluster {
            opacity: 0.4 !important;
            transition: opacity 0.3s ease;
          }
        `}</style>
      )}
      {showStats && (
        <MapStats 
          totalCases={validCases.length} 
          visibleCases={visibleCount}
          clustered={enableClustering}
          showHeatmap={showHeatmap}
          userLocation={userLocation}
        />
      )}
      
      {/* Viewport Case Count Overlay - Bottom Left */}
      <ViewportCaseCountOverlay 
        caseCount={validCases.length}
        loading={loadingViewport}
        currentZoom={currentZoom}
      />
      
      <MapContainer
        center={userLocation || center}
        zoom={userLocation ? 10 : zoom}
        className="h-full w-full"
        data-testid="map-container"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Interstate Corridor Overlays */}
        {showCorridors && corridors.map((corridor) => (
          <Polyline
            key={corridor.id}
            positions={corridor.coordinates}
            pathOptions={{
              color: corridor.color,
              weight: 4,
              opacity: 0.7,
              dashArray: corridor.highlighted ? null : '10, 10',
            }}
          >
            <Tooltip sticky>
              <div className="text-sm font-medium">{corridor.name}</div>
              <div className="text-xs text-gray-600">{corridor.description}</div>
            </Tooltip>
          </Polyline>
        ))}
        
        <FitBounds cases={validCases} shouldFit={hasFittedBounds && fitBoundsOnLoad && !userLocation} />
        <ViewportTracker cases={validCases} onVisibleCasesChange={handleVisibleCasesChange} />
        <ViewportBoundsTracker onViewportChange={onViewportChange} onZoomChange={setCurrentZoom} />
        <MapController onMapReady={onMapReady} selectedCaseId={selectedCaseId} cases={validCases} />
        
        {/* User Location Marker */}
        {userLocation && (
          <UserLocationMarker position={userLocation} radius={userLocationRadius} />
        )}
        
        {/* Route Waypoints and Path */}
        {routeWaypoints.length > 0 && (
          <>
            {/* Route polyline connecting waypoints */}
            {routeWaypoints.length >= 2 && (
              <Polyline
                positions={routeWaypoints.map(wp => [wp.lat, wp.lng])}
                pathOptions={{
                  color: '#3b82f6',
                  weight: 4,
                  opacity: 0.8,
                  dashArray: '10, 5',
                }}
              />
            )}
            
            {/* Route radius circles around each waypoint */}
            {routeWaypoints.map((wp, index) => (
              <Circle
                key={`route-radius-${wp.id}`}
                center={[wp.lat, wp.lng]}
                radius={routeRadius * 1609.34}
                pathOptions={{
                  color: getWaypointColor(wp.label),
                  fillColor: getWaypointColor(wp.label),
                  fillOpacity: 0.08,
                  weight: 1,
                  dashArray: '5, 5'
                }}
              />
            ))}
            
            {/* Waypoint markers */}
            {routeWaypoints.map((wp, index) => (
              <Marker
                key={`route-wp-${wp.id}`}
                position={[wp.lat, wp.lng]}
                icon={createWaypointIcon(index + 1, wp.label)}
              >
                <Popup>
                  <div className="p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-sm">{index + 1}.</span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {wp.label?.replace('_', ' ') || 'Waypoint'}
                      </span>
                    </div>
                    <p className="font-medium text-sm">{wp.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{wp.fullName}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </>
        )}
        
        {/* Heatmap Layer */}
        {showHeatmap && (
          <HeatmapLayer points={validCases} />
        )}
        
        {/* Clustered markers for better performance - hide when heatmap is shown */}
        {!showHeatmap && enableClustering && (
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={createClusterCustomIcon}
            maxClusterRadius={60}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            disableClusteringAtZoom={15}
            animate={true}
          >
            {validCases.map(renderMarker)}
          </MarkerClusterGroup>
        )}
        
        {/* Individual markers when clustering and heatmap are disabled */}
        {!showHeatmap && !enableClustering && validCases.map(renderMarker)}
        
        {/* Investigative Cluster Overlay */}
        <ClusterLayer
          clusters={clusterData}
          visible={clustersVisible}
          onClusterClick={(c) => setSelectedCluster(c)}
        />
      </MapContainer>
      
      {/* Cluster Toggle Button - Bottom Right */}
      <button
        onClick={() => setClustersVisible(v => !v)}
        data-testid="cluster-toggle-btn"
        className={`absolute bottom-4 right-4 z-[1000] px-3 py-2 rounded-full text-xs font-semibold shadow-lg transition-all ${
          clustersVisible
            ? 'bg-purple-600 text-white border-2 border-purple-400'
            : 'bg-white/95 dark:bg-slate-900/95 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-purple-400'
        }`}
        style={{ fontFamily: 'monospace' }}
      >
        {'\u25C8'} Clusters {clusterData.length > 0 ? `(${clusterData.length})` : ''}
      </button>
      
      {/* Cluster Detail Panel */}
      {selectedCluster && (
        <ClusterPanel
          cluster={selectedCluster}
          onClose={() => setSelectedCluster(null)}
        />
      )}
    </div>
  );
};
